import {Injectable, NgZone, signal} from '@angular/core';
import {Howl} from 'howler';
import {AudioTrack} from './audio.service';
import {AudioStateService} from './audio-state.service';
import {AudioMobileService} from './audio-mobile.service';
import {AudioEffectsService} from './audio-effects.service';
import {AudioPreloadService, CacheEntry} from './audio-preload.service';

/**
 * Service responsible for Howl instance management and audio playback control
 * Extracted from AudioService to improve maintainability while preserving exact functionality
 */
@Injectable({
  providedIn: 'root'
})
export class AudioPlayerService {

  // ============================================================================
  // HOWL INSTANCE MANAGEMENT - EXACT preservation from AudioService
  // ============================================================================

  private howlSound = signal<Howl | null>(null);
  private timeUpdateInterval = signal<any>(null);

  // Race condition prevention flags - EXACT preservation
  private isChangingTrack = false;
  private isCreatingHowl = false;

  constructor(
    private ngZone: NgZone,
    private stateService: AudioStateService,
    private mobileService: AudioMobileService,
    private effectsService: AudioEffectsService,
    private preloadService: AudioPreloadService
  ) {
  }

  // ============================================================================
  // TRACK PLAYBACK
  // ============================================================================

  /**
   * Play a specific track by index. Routes through the preload cache when
   * possible (instant promotion on cache-hit `ready`, deferred promotion on
   * `loading` via the entry's onload handler), and falls through to the
   * legacy `createAndPlayHowl` path on `error` or absent entries.
   */
  playTrack(index: number, isLooping: boolean = false, userInitiated: boolean = false): void {
    console.log(`Playing track at index ${index}, isLooping: ${isLooping}, userInitiated: ${userInitiated}`);
    const tracks = this.stateService.tracks();

    if (index < 0 || index >= tracks.length) {
      console.warn(`Invalid track index: ${index}, max: ${tracks.length - 1}`);
      return;
    }

    if (this.isCreatingHowl) {
      console.log('Currently creating Howl instance (fallback path active), delaying playTrack request');
      setTimeout(() => this.playTrack(index, isLooping, userInitiated), 100);
      return;
    }

    const currentIndex = this.stateService.currentTrackIndex();
    if (index === currentIndex && this.stateService.isPlaying() && !isLooping) {
      console.log('Already playing this track - no need to restart it');
      return;
    }

    this.stateService.setIsLoopingTrack(isLooping);
    this.stateService.setWasUserInitiated(userInitiated);
    this.isChangingTrack = true;
    this.stateService.setCurrentTime(0);

    if (userInitiated) {
      this.stateService.setLastUserInteractionTime(Date.now());
      console.log('User initiated playback - setting lastUserInteractionTime');
      if (this.mobileService.isMobile()) {
        this.mobileService.setBypassFadeCompletely(true);
      }
    }

    // Reset pending-active — overwritten by the loading branch below if we land there.
    // Without this, a stale 'loading' entry from a prior navigation could promote itself
    // via its onload after the user has already moved on.
    this.preloadService.markPendingActive(null);

    // Demote the currently active Howl (if any) before sliding the window.
    const oldHowl = this.howlSound();
    const oldTrack = tracks[currentIndex];
    if (oldHowl && oldTrack && currentIndex !== index) {
      this.stateService.setIsPauseResumeOperation(true);
      try {
        oldHowl.pause();
        oldHowl.seek(oldTrack.startTime || 0);
      } catch (e) {
        console.warn('Demote-pause failed (likely Howl already stopped):', e);
      }
      setTimeout(() => this.stateService.setIsPauseResumeOperation(false), 100);
    }

    this.stateService.setCurrentTrackIndex(index);
    this.slideAndDiff(index);

    const entry = this.preloadService.get(index);
    if (!entry) {
      console.warn(`No cache entry for index ${index} after slideAndDiff; falling back to legacy path`);
      this.createAndPlayHowl(tracks[index], isLooping);
      return;
    }

    if (entry.state === 'ready') {
      this.stateService.setIsLoading(false);
      this.promoteCachedEntry(index, tracks[index]);
      return;
    }

    if (entry.state === 'loading') {
      this.howlSound.set(entry.howl);
      this.stateService.setIsLoading(true);
      this.preloadService.markPendingActive(index);
      return;
    }

    // entry.state === 'error' — evict and fall back
    this.preloadService.evict(index);
    this.createAndPlayHowl(tracks[index], isLooping);
  }

  /**
   * Toggle play/pause state
   * EXACT preservation from AudioService lines 253-333
   */
  togglePlayPause(): void {
    const sound = this.howlSound();
    if (!sound) {
      console.warn('No audio loaded for play/pause');
      return;
    }

    this.stateService.setIsPauseResumeOperation(true);

    if (this.stateService.isPlaying()) {
      console.log('User pausing audio - current state: playing');
      // Set state first to prevent race conditions
      this.stateService.setIsPlaying(false);
      this.clearTimeUpdateInterval();
      sound.pause();
      console.log('Audio paused successfully - new state: paused');
    } else {
      console.log('User resuming audio - current state: paused');

      // Restore position if we were previously playing
      const currentTime = this.stateService.currentTime();
      if (currentTime > 0) {
        console.log(`Seeking to previous position: ${currentTime}s`);
        sound.seek(currentTime);
      }

      // Set state before starting playback
      this.stateService.setIsPlaying(true);
      this.startTimeUpdate();
      sound.play();
      console.log('Audio resumed successfully - new state: playing');

      // Apply appropriate fade effect
      if (this.mobileService.isMobile()) {
        this.effectsService.fadeAudioIn(true); // Keep current volume for mobile
      } else {
        this.effectsService.fadeAudioIn();
      }
    }

    // Reset pause/resume flag after a brief delay
    setTimeout(() => {
      this.stateService.setIsPauseResumeOperation(false);
    }, 100);
  }

  // ============================================================================
  // HOWL LIFECYCLE MANAGEMENT
  // ============================================================================

  /**
   * Clean up audio resources.
   *
   * `cleanup(true)` (track-change cleanup): no-op for Howl lifetimes — the
   *   demote step in `playTrack` paused the prior active Howl, and the cache
   *   owns when entries unload (via slideWindow eviction).
   * `cleanup(false)` (session-end cleanup, e.g. setTracks/slideshow close):
   *   `preloadService.clear()` unloads every cached Howl; `howlSound` is reset.
   */
  cleanup(isTrackChange: boolean = false): void {
    console.log('Cleaning up audio resources...', { isTrackChange });

    this.clearTimeUpdateInterval();

    if (isTrackChange) {
      // Track-change cleanup: the demote step in playTrack already handled
      // the active Howl. Nothing to do here. We do NOT unload — the cache
      // owns Howl lifetimes.
      this.isCreatingHowl = false;
      this.isChangingTrack = false;
      return;
    }

    // Session-end cleanup: drop all cached Howls.
    this.preloadService.clear();
    this.howlSound.set(null);
    this.isCreatingHowl = false;
    this.isChangingTrack = false;
    this.stateService.setIsLoading(false);
  }

  /**
   * Get the current Howl sound instance
   */
  getHowlSound(): Howl | null {
    return this.howlSound();
  }

  // ============================================================================
  // PUBLIC ACCESSORS — track-change state
  // ============================================================================

  /**
   * Check if currently changing tracks
   */
  getIsChangingTrack(): boolean {
    return this.isChangingTrack;
  }

  /**
   * Set track changing state
   */
  setIsChangingTrack(value: boolean): void {
    this.isChangingTrack = value;
  }

  // ============================================================================
  // PUBLIC ACCESSORS — Howl-creation state
  // ============================================================================

  /**
   * Check if currently creating Howl instance
   */
  getIsCreatingHowl(): boolean {
    return this.isCreatingHowl;
  }

  // ============================================================================
  // PUBLIC ACCESSORS
  // ============================================================================

  /**
   * Force stop current audio immediately
   */
  stop(immediate: boolean = false): void {
    const sound = this.howlSound();
    if (!sound) return;

    if (immediate) {
      this.cleanup(false); // Normal cleanup for immediate stop
    } else {
      this.effectsService.fadeAudioOut(() => this.cleanup(false)); // Normal cleanup after fade
    }

    this.stateService.setIsPlaying(false);
    this.stateService.setCurrentTime(0);
  }

  /**
   * Build a cached Howl for `track` at `index`. The Howl is constructed but
   * not played; downloads kick off via Howler's default `preload: true`.
   *
   * The onload handler runs the promotion path if this index is the
   * pending-active one. The onloaderror handler marks state and falls
   * through to legacy createAndPlayHowl if it's the pending-active.
   */
  private createCacheEntry(track: AudioTrack, index: number): CacheEntry {
    const generation = this.preloadService.currentGeneration();
    const shouldBypassFade = this.mobileService.shouldBypassFade();

    const howl = new Howl({
      src: [track.url],
      volume: shouldBypassFade ? this.stateService.volume() : 0,
      onload: () => {
        if (!this.preloadService.isCurrentGeneration(generation)) return;
        this.preloadService.setState(index, 'ready');
        if (this.preloadService.consumePendingActive(index)) {
          this.promoteCachedEntry(index, track);
        }
      },
      onloaderror: (_id, error) => {
        if (!this.preloadService.isCurrentGeneration(generation)) return;
        console.error(`Howl load error for index ${index}:`, error);
        this.preloadService.setState(index, 'error');
        if (this.preloadService.consumePendingActive(index)) {
          this.preloadService.evict(index);
          this.createAndPlayHowl(track);
        }
      },
      onplay: () => {
        console.log(`Howl onplay event triggered for index ${index}`);
        this.stateService.setIsPlaying(true);
        this.startTimeUpdate();
        if (!shouldBypassFade) {
          this.effectsService.fadeAudioIn();
        }
      },
      onpause: () => {
        console.log(`Howl onpause event triggered for index ${index}`);
        if (!this.stateService.getIsPauseResumeOperation()) {
          this.stateService.setIsPlaying(false);
          this.clearTimeUpdateInterval();
        }
      },
      onstop: () => {
        console.log(`Howl onstop event triggered for index ${index}`);
        this.stateService.setIsPlaying(false);
        this.clearTimeUpdateInterval();
      },
      onend: () => {
        console.log(`Howl onend event triggered for index ${index}`);
        this.stateService.setIsPlaying(false);
        this.clearTimeUpdateInterval();
      },
      onplayerror: (_id, error) => {
        console.error(`Howl play error for index ${index}:`, error);
        this.stateService.incrementAutoplayFailures();
        if (this.stateService.getAutoplayFailures() > 2) {
          this.stateService.setIsAutoplayBlocked(true);
        }
      },
    });

    return { howl, state: 'loading', generation };
  }

  /**
   * Promote a ready cache entry to active and start playback.
   *
   * Resets volume to 0 so fadeAudioIn (triggered from onplay) starts from a
   * clean baseline regardless of any volume changes that happened while the
   * entry was idle in the cache.
   */
  private promoteCachedEntry(index: number, track: AudioTrack): void {
    const entry = this.preloadService.get(index);
    if (!entry || entry.state !== 'ready') {
      console.warn(`promoteCachedEntry called with non-ready entry at ${index}`);
      return;
    }
    const startTime = track.startTime || 0;
    const shouldBypassFade = this.mobileService.shouldBypassFade();

    this.howlSound.set(entry.howl);
    entry.howl.volume(shouldBypassFade ? this.stateService.volume() : 0);
    entry.howl.seek(startTime);
    this.stateService.setCurrentTime(startTime);

    const duration = entry.howl.duration();
    this.stateService.setDuration(duration);
    if (track.endTime === undefined) {
      this.stateService.setActualAudioDuration(duration);
    }

    this.stateService.setIsLoading(false);
    this.isChangingTrack = false;

    if (!entry.howl.playing()) {
      entry.howl.play();
    }

    setTimeout(() => {
      if (!this.mobileService.isMobile()) {
        this.mobileService.setBypassFadeCompletely(false);
      }
    }, 1000);
  }

  /**
   * Build the initial cache for a fresh track list. Called from
   * AudioService.setTracks after the state service updates.
   */
  primeCache(tracks: AudioTrack[]): void {
    this.preloadService.clear();
    if (tracks.length === 0) return;
    const window = AudioPreloadService.computeWindow(0, tracks.length);
    for (const index of window) {
      const entry = this.createCacheEntry(tracks[index], index);
      this.preloadService.set(index, entry);
    }
  }

  /**
   * Recompute the window for `toIndex` and apply the diff: evict entries
   * that fell out of the window, create entries for indices that entered.
   */
  private slideAndDiff(toIndex: number): void {
    const tracks = this.stateService.tracks();
    if (tracks.length === 0) return;
    const { toEvict, toCreate } = this.preloadService.computeDiff(toIndex, tracks.length);
    for (const i of toEvict) this.preloadService.evict(i);
    for (const i of toCreate) {
      const entry = this.createCacheEntry(tracks[i], i);
      this.preloadService.set(i, entry);
    }
  }

  /**
   * Create and play a new Howl instance
   * EXACT preservation from AudioService createAndPlayHowl method
   */
  private createAndPlayHowl(track: AudioTrack, isLooping: boolean = false): void {
    if (this.isCreatingHowl) {
      console.log('Already creating a Howl instance, ignoring duplicate request');
      return;
    }

    this.isCreatingHowl = true;
    const trackLoadId = this.stateService.incrementTrackLoadId();

    // Cache demote handled by playTrack; legacy fallback path skips the redundant cleanup.

    // Show loading indicator unless this is a loop operation
    if (!this.stateService.getIsLoopingTrack()) {
      this.stateService.setIsLoading(true);
    }

    // For track changes on older devices, add delay to ensure cleanup completes
    const isOlderDevice = this.detectPotentialRaceCondition();
    const creationDelay = isOlderDevice ? 150 : 50; // Increased delay for older devices

    setTimeout(() => {
      // Double-check that this is still the current track load request
      if (trackLoadId !== this.stateService.getCurrentTrackLoadId()) {
        console.log('Track load request outdated, cancelling Howl creation');
        this.isCreatingHowl = false;
        return;
      }

      const shouldBypassFade = this.mobileService.shouldBypassFade();
      const startTime = track.startTime || 0;

      console.log(`Creating new Howl instance for track: ${track.title}`);

      // Create new Howl instance with exact configuration
      const howl = new Howl({
        src: [track.url],
        volume: shouldBypassFade ? this.stateService.volume() : 0,
        onload: () => this.finishHowlCreation(track, isLooping, trackLoadId),
        onplay: () => {
          console.log('Howl onplay event triggered');
          this.stateService.setIsPlaying(true);
          this.startTimeUpdate();
          if (!shouldBypassFade) {
            this.effectsService.fadeAudioIn();
          }
        },
        onpause: () => {
          console.log('Howl onpause event triggered');
          // Only update state if this wasn't a manual pause/resume operation
          if (!this.stateService.getIsPauseResumeOperation()) {
            this.stateService.setIsPlaying(false);
            this.clearTimeUpdateInterval();
          }
        },
        onstop: () => {
          console.log('Howl onstop event triggered');
          this.stateService.setIsPlaying(false);
          this.clearTimeUpdateInterval();
        },
        onend: () => {
          console.log('Howl onend event triggered');
          this.stateService.setIsPlaying(false);
          this.clearTimeUpdateInterval();
        },
        onloaderror: (id, error) => {
          console.error('Howl load error:', error);
          this.stateService.setIsLoading(false);
          this.isCreatingHowl = false;
        },
        onplayerror: (id, error) => {
          console.error('Howl play error:', error);
          this.stateService.incrementAutoplayFailures();
          if (this.stateService.getAutoplayFailures() > 2) {
            this.stateService.setIsAutoplayBlocked(true);
          }
          this.isCreatingHowl = false;
        }
      });

      this.howlSound.set(howl);
    }, creationDelay);
  }

  /**
   * Complete Howl creation and start playback
   * EXACT preservation from AudioService finishHowlCreation method
   */
  private finishHowlCreation(track: AudioTrack, isLooping: boolean, trackLoadId: number): void {
    // Check if this is still the current track load request
    if (trackLoadId !== this.stateService.getCurrentTrackLoadId()) {
      console.log('Howl load completed for outdated track request, ignoring');
      return;
    }

    const sound = this.howlSound();
    if (!sound) {
      console.warn('Howl sound is null in finishHowlCreation');
      return;
    }

    console.log('Howl loaded successfully');

    // Update duration
    const duration = sound.duration();
    this.stateService.setDuration(duration);

    // For gaming mode tracks (no endTime), set the actual audio duration
    if (track.endTime === undefined) {
      this.stateService.setActualAudioDuration(duration);
    }

    // Set start time if specified
    const startTime = track.startTime || 0;
    if (startTime > 0) {
      sound.seek(startTime);
      this.stateService.setCurrentTime(startTime);
    }

    // Clear loading state
    this.stateService.setIsLoading(false);
    this.isCreatingHowl = false;
    this.isChangingTrack = false;

    // Start playback
    sound.play();

    // Reset bypass fade flag after playback starts to allow fades for subsequent tracks
    setTimeout(() => {
      if (!this.mobileService.isMobile()) {
        this.mobileService.setBypassFadeCompletely(false);
      }
    }, 1000); // Reset after 1 second to allow initial playback to start
  }

  /**
   * Start time update interval for progress tracking
   */
  private startTimeUpdate(): void {
    this.clearTimeUpdateInterval();

    const interval = setInterval(() => {
      const sound = this.howlSound();
      if (sound && this.stateService.isPlaying()) {
        const currentTime = sound.seek() as number;
        this.stateService.setCurrentTime(currentTime);

        // Check if we've reached the end time and should fade out
        // Only apply end time checking for tracks that have a defined endTime (normal mode)
        const currentTrack = this.stateService.currentTrack();
        if (currentTrack?.endTime !== undefined && currentTime >= currentTrack.endTime - 1.0) {
          console.log(`Approaching end time ${currentTrack.endTime}s at current time ${currentTime}s, fading out`);
          this.stateService.setIsPlaying(false);
          this.clearTimeUpdateInterval();

          // Fade out before stopping
          this.effectsService.fadeAudioOut(() => {
            sound.stop();
          });
        }
      }
    }, 250); // Update every 250ms for more precise end_time detection

    this.timeUpdateInterval.set(interval);
  }

  /**
   * Clear time update interval
   */
  private clearTimeUpdateInterval(): void {
    const interval = this.timeUpdateInterval();
    if (interval) {
      clearInterval(interval);
      this.timeUpdateInterval.set(null);
    }
  }

  private detectPotentialRaceCondition(): boolean {
    // Check if we just had a cleanup recently (within last 200ms)
    // This helps detect potential race conditions with the cleanup timeout
    const currentSound = this.howlSound();

    // If we don't have a current sound but we're not in the process of creating one,
    // and we were recently changing tracks, there might be a race condition
    const noSoundButWasChanging = !currentSound && !this.isCreatingHowl && this.isChangingTrack;

    // Check user agent for known slower devices (2017 Macs typically have older Safari)
    const userAgent = navigator.userAgent.toLowerCase();
    const hasOlderSafari = /safari\/60[0-9]/.test(userAgent); // Safari 60x versions from around 2017
    const hasOlderWebKit = /webkit\/60[0-4]/.test(userAgent); // WebKit 60x versions
    const isMac2017Era = /mac os x 10_1[23]/.test(userAgent); // macOS 10.12-10.13 (2017 era)

    // Simple performance check - older devices will be slower
    const start = performance.now();
    let sum = 0;
    for (let i = 0; i < 1000; i++) {
      sum += Math.random();
    }
    const computeTime = performance.now() - start;
    const isSlowerDevice = computeTime > 2; // Should complete in <2ms on modern devices

    const raceConditionLikely = noSoundButWasChanging || hasOlderSafari || hasOlderWebKit || isMac2017Era || isSlowerDevice;

    if (raceConditionLikely) {
      console.log('Potential race condition detected (likely older Mac):', {
        noSoundButWasChanging,
        hasOlderSafari,
        hasOlderWebKit,
        isMac2017Era,
        computeTime: computeTime.toFixed(2) + 'ms',
        isSlowerDevice,
        userAgent: userAgent.substring(0, 100)
      });
    }

    return raceConditionLikely;
  }
}
